import { Box, Grid, SxProps, Typography, useMediaQuery } from '@mui/material';
import { ProfileRolesEnum } from '@protos/user_management/ums.db_pb';
import { ReactNode } from 'react';
import lessonIcon from '../../../assets/icons/lesson.png';
import resourcesIcon from '../../../assets/icons/resources-paper.png';
import rightArrow from '../../../assets/icons/rightArrow.png';
import { pxToRem, pxTovW } from '../../../commonUtils/resizeUtils';
import { IStyles } from '../../../commonUtils/styleUtils';
import { theme } from '../../../theme/themeProvider';

const styles: IStyles = {
  root: {
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxSizing: 'border-box',
    width: { xs: '100%', md: '100%' },
  },
  headingTypo: {
    display: '-webkit-box',
    WebkitLineClamp: { xs: 1, md: 2 },
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  secondaryHeadings: {
    display: 'flex',
    alignItems: 'center',
    // justifyContent: 'space-between',
    gap: { xs: pxToRem(10), md: pxTovW(20) },
    mt: { xs: pxToRem(10), md: 'unset' },
    flexBasis: 1,
    width: '100%',
  },

  // CardDetails
  cardDetailsRoot: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(5), md: pxTovW(5) },
    width: { xs: 'max-content', md: 'max-content' },
  },
};

interface IProps {
  viewAllClickHandler: () => void;
  title: string;
  lessonPlanCount?: number | undefined;
  resourcesCount?: number;
  homeWorkCount?: number | undefined;
  questionsCount?: number | undefined;
  profileType?: ProfileRolesEnum;
  rootStyle?: SxProps;
  offlineSubText?: string;
  children?: ReactNode;
  showViewAllOnMobile?: boolean;
  isLocked?: boolean;
  dataId?: string;
  questionsDataId?: string;
  questionDataId?: string;
}

export const TopicCardPrime = (props: IProps) => {
  const {
    title,
    viewAllClickHandler,
    lessonPlanCount,
    resourcesCount,
    homeWorkCount,
    questionsCount,
    profileType,
    rootStyle,
    children,
    isLocked,
    dataId,
    questionsDataId: questionsDataIdProp,
    questionDataId: questionDataIdProp,
  } = props;

  const questionsDataId =
    questionsDataIdProp ||
    questionDataIdProp ||
    (dataId ? `${dataId}-questions` : 'questions-count');

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  let rootSx = { ...styles.root };
  if (rootStyle) rootSx = { ...rootSx, ...rootStyle };

  return (
    <Box
      sx={{ ...rootSx, opacity: isLocked ? 0.5 : 1 }}
      onClick={viewAllClickHandler}
      data-id={dataId || 'topic-card'}
    >
      <Grid container>
        <Grid item xs={12} md={7.5}>
          <Typography
            variant="h3"
            fontWeight="bold"
            fontFamily="Lato"
            sx={styles.headingTypo}
          >
            {title}
          </Typography>
        </Grid>

        <Grid
          item
          xs={12}
          md={4.5}
          sx={{
            ...styles.secondaryHeadings,
            justifyContent:
              [
                lessonPlanCount,
                resourcesCount,
                homeWorkCount,
                questionsCount,
              ].filter(Boolean).length === 1
                ? 'flex-end'
                : 'space-between',
          }}
        >
          {lessonPlanCount ? (
            <CardDetails
              iconName={lessonIcon}
              count={lessonPlanCount}
              countText={
                profileType === ProfileRolesEnum.PROFILE_ROLE_STUDENT
                  ? 'Lesson'
                  : 'Lesson Plan'
              }
              dataId={dataId ? `${dataId}-lesson-plan` : undefined}
            />
          ) : null}

          {resourcesCount ? (
            <CardDetails
              iconName={resourcesIcon}
              count={resourcesCount}
              countText={'Resource'}
              dataId={dataId ? `${dataId}-resource` : undefined}
            />
          ) : null}

          {homeWorkCount ? (
            <CardDetails
              iconName={lessonIcon}
              count={homeWorkCount}
              countText={'Assessment'}
              dataId={dataId ? `${dataId}-homework` : undefined}
            />
          ) : null}

          {questionsCount ? (
            <CardDetails
              iconName={resourcesIcon}
              count={questionsCount}
              countText={'Question'}
              dataId={questionsDataId}
            />
          ) : null}
        </Grid>
      </Grid>

      {isMobile && !children && (
        <img
          alt="chapter"
          src={rightArrow}
          onClick={viewAllClickHandler}
          style={{
            height: pxToRem(12.5),
            width: pxToRem(7),
            marginLeft: 'auto',
            marginRight: pxToRem(10),
          }}
        ></img>
      )}

      {children}
    </Box>
  );
};

interface ICardDetailsProps {
  iconName?: string;
  count: number;
  countText: string;
  dataId?: string;
}
const CardDetails = (props: ICardDetailsProps) => {
  const { iconName, count, countText, dataId } = props;

  return (
    <Box
      sx={styles.cardDetailsRoot}
      {...(dataId ? { 'data-id': dataId } : {})}
    >
      <img
        alt="topic"
        src={iconName}
        style={{ height: pxToRem(17), width: pxToRem(14) }}
      />
      <Typography
        variant="bodyText"
        fontWeight="bold"
        {...(dataId ? { 'data-id': `${dataId}-value` } : {})}
      >
        {count}
      </Typography>
      <Typography
        variant="h5"
        {...(dataId ? { 'data-id': `${dataId}-text` } : {})}
      >
        {countText}
        {count > 1 ? 's' : ''}
      </Typography>
    </Box>
  );
};
